import { TFunction } from 'i18next';

import { BidAskItem } from '../../../shared/types';
import { PriceDataPoint } from '../../../types';
import {
  defaultTooltipForSeriesUsing2DimensionData,
  ExternalSeriesGenerator,
  getDefaultToolptipFormatter,
  PriceViewerSeries,
} from './base';
import { fillPriceDates } from './utils';

class RetailInstitutionalTrades extends ExternalSeriesGenerator {
  isSeparateFromPriceChart = true;
  forceYAxisNotToScale = true;
  tradesType: string;
  bitColor: string;
  askColor: string;
  askBidChartVersion: string;
  bidAskData: readonly BidAskItem[];

  constructor(
    bidAskData: readonly BidAskItem[],
    tradesType: string,
    t: TFunction,
    bitColor: string,
    askColor: string,
    askBidChartVersion: string
  ) {
    super(t);
    this.bidAskData = bidAskData;
    this.tradesType = tradesType;
    this.bitColor = bitColor;
    this.askColor = askColor;
    this.askBidChartVersion = askBidChartVersion;
  }

  getChartName(): string {
    return this.t(this.tradesType);
  }

  getSeries(dataPoints: ReadonlyArray<PriceDataPoint>): PriceViewerSeries[] {
    const priceDates = dataPoints.map(item => item.date.toString());

    const askRatio: string[] = [];
    const bidRatio: string[] = [];
    const netAskBidRatio: string[] = [];
    if (this.tradesType == 'retail-trades') {
      for (const item of this.bidAskData) {
        const totalAsk = item.total_ask;
        const totalBid = item.total_bid;
        const totalAskBid = item.total_ask + item.total_bid;
        const askPercentage = (totalAsk / totalAskBid) * 100;
        const bidPercentage = (totalBid / totalAskBid) * 100;
        askRatio.push(askPercentage.toFixed(0));
        bidRatio.push(bidPercentage.toFixed(0));
        netAskBidRatio.push((askPercentage - bidPercentage).toFixed(0));
      }
    } else {
      for (const item of this.bidAskData) {
        const volumePerAsk = item.total_ask_volume / item.total_ask;
        const volumePerBid = item.total_bid_volume / item.total_bid;
        const askPercentage =
          (volumePerAsk / (volumePerAsk + volumePerBid)) * 100;
        const bidPercentage =
          (volumePerBid / (volumePerAsk + volumePerBid)) * 100;
        askRatio.push(askPercentage.toFixed(0));
        bidRatio.push(bidPercentage.toFixed(0));
        netAskBidRatio.push((askPercentage - bidPercentage).toFixed(0));
      }
    }
    const dates = this.bidAskData.map(({ date }) => date);

    // match price dates and series dates to achieve smooth panning

    return this.askBidChartVersion === 'v1'
      ? [
          {
            name: `${this.t(this.tradesType)} (${this.t('ask')})`,
            echartSeries: {
              type: 'bar',
              stack: this.tradesType,
              data: fillPriceDates(priceDates, askRatio, dates),
              label: {
                show: true,
              },
              itemStyle: {
                opacity: 0.8,
                color: this.askColor,
              },
              markLine: {
                symbol: ['none', 'none'],
                data: [
                  {
                    name: 'average line',
                    yAxis: 50,
                    lineStyle: {
                      color: 'yellow',
                      width: 2,
                      type: 'solid',
                      opacity: 0.8,
                    },
                  },
                ],
              },
            },
            tooltipFormatter: defaultTooltipForSeriesUsing2DimensionData,
          },
          {
            name: `${this.t(this.tradesType)} (${this.t('bid')})`,
            echartSeries: {
              type: 'bar',
              stack: this.tradesType,
              data: fillPriceDates(priceDates, bidRatio, dates),
              label: {
                show: true,
              },
              itemStyle: {
                opacity: 0.8,
                color: this.bitColor,
              },
              markLine: {
                symbol: ['none', 'none'],
                data: [
                  {
                    name: 'average line',
                    yAxis: 50,
                    lineStyle: {
                      color: 'yellow',
                      width: 2,
                      type: 'solid',
                      opacity: 0.8,
                    },
                  },
                ],
              },
            },
            tooltipFormatter: defaultTooltipForSeriesUsing2DimensionData,
          },
        ]
      : [
          {
            name: `${this.t(this.tradesType)}`,
            echartSeries: {
              type: 'bar',
              data: fillPriceDates(priceDates, netAskBidRatio, dates),
              label: {
                show: true,
              },
              itemStyle: {
                opacity: 0.8,
                color: ({ value }) => {
                  return Number.parseInt(value[1], 10) > 0
                    ? this.askColor
                    : this.bitColor;
                },
              },
            },
            tooltipFormatter: getDefaultToolptipFormatter('2d', 'number', {
              shouldChangeColorByValue: true,
            }),
          },
        ];
  }
}

export default RetailInstitutionalTrades;
